import { useCallback, useEffect, useRef, useState } from "react";
import IntelexFrame, { intelexCsrfToken } from "../intelex/IntelexFrame";
import { confirmAction } from "../intelex/ConfirmationDialog";
import { dataItem, displayDate, intelexRequest } from "../intelex/api";

function remainingText(milliseconds) {
  const total = Math.max(0, Math.ceil(milliseconds / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${hours ? `${hours}:` : ""}${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function Choice({ option, checked, type, name, onChange }) {
  return (
    <label className="intelex-exam-choice">
      <input type={type} name={name} checked={checked} onChange={onChange} />
      <span>{option.label}</span>
    </label>
  );
}

function ExamQuestion({ question, answer, onChange }) {
  const options = question.config?.options || [];
  if (question.type === "single_choice" || question.type === "true_false")
    return (
      <div className="intelex-exam-choices">
        {options.map((option) => (
          <Choice
            key={option.id}
            option={option}
            type="radio"
            name={`exam-${question.id}`}
            checked={String(answer ?? "") === String(option.id)}
            onChange={() => onChange(option.id)}
          />
        ))}
      </div>
    );
  if (question.type === "multiple_choice") {
    const selected = Array.isArray(answer) ? answer : [];
    return (
      <div className="intelex-exam-choices">
        {options.map((option) => (
          <Choice
            key={option.id}
            option={option}
            type="checkbox"
            checked={selected.includes(option.id)}
            onChange={() =>
              onChange(
                selected.includes(option.id)
                  ? selected.filter((id) => id !== option.id)
                  : [...selected, option.id],
              )
            }
          />
        ))}
      </div>
    );
  }
  if (question.type === "short_text")
    return (
      <input
        className="intelex-exam-answer-input"
        type="text"
        maxLength="1000"
        autoComplete="off"
        value={answer ?? ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Enter your answer"
      />
    );
  if (question.type === "written_response")
    return question.config?.multiline ? (
      <textarea
        className="intelex-exam-answer-input intelex-exam-written-answer"
        rows="7"
        maxLength="5000"
        value={answer ?? ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Enter your response"
      />
    ) : (
      <input
        className="intelex-exam-answer-input"
        type="text"
        maxLength="5000"
        autoComplete="off"
        value={answer ?? ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Enter your response"
      />
    );
  if (question.type === "number")
    return (
      <input
        className="intelex-exam-answer-input"
        type="number"
        step="any"
        value={answer ?? ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Enter a number"
      />
    );
  if (question.type === "matching") {
    const selected =
      answer && typeof answer === "object" && !Array.isArray(answer)
        ? answer
        : {};
    return (
      <div className="intelex-exam-matching">
        {(question.config?.left || []).map((left) => (
          <label key={left.id}>
            <span>{left.label}</span>
            <select
              value={selected[left.id] || ""}
              onChange={(event) =>
                onChange({ ...selected, [left.id]: event.target.value })
              }
            >
              <option value="">Select the match</option>
              {(question.config?.right || []).map((right) => (
                <option key={right.id} value={right.id}>
                  {right.label}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
    );
  }
  if (question.type === "ordering") {
    const order =
      Array.isArray(answer) && answer.length
        ? answer
        : options.map((option) => option.id);
    const move = (index, direction) => {
      const target = index + direction;
      if (target < 0 || target >= order.length) return;
      const next = [...order];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      onChange(next);
    };
    return (
      <div className="intelex-exam-ordering">
        <p>Arrange these items in the correct order.</p>
        {order.map((id, index) => {
          const option = options.find((item) => item.id === id);
          return (
            <div key={id}>
              <span>{index + 1}</span>
              <strong>{option?.label || id}</strong>
              <button
                type="button"
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                Move up
              </button>
              <button
                type="button"
                disabled={index === order.length - 1}
                onClick={() => move(index, 1)}
              >
                Move down
              </button>
            </div>
          );
        })}
        <button
          type="button"
          className="intelex-exam-confirm-order"
          onClick={() => onChange(order)}
        >
          Use this order
        </button>
      </div>
    );
  }
  return null;
}

function answerComplete(question, answer) {
  if (answer === null || answer === undefined) return false;
  if (typeof answer === "string") return answer.trim().length > 0;
  if (question.type === "matching") {
    const left = question.config?.left || [];
    return (
      answer &&
      typeof answer === "object" &&
      !Array.isArray(answer) &&
      left.length > 0 &&
      left.every((item) => String(answer[item.id] || "").trim())
    );
  }
  if (question.type === "ordering") {
    const options = question.config?.options || [];
    return (
      Array.isArray(answer) &&
      options.length > 0 &&
      answer.length === options.length
    );
  }
  if (Array.isArray(answer)) return answer.length > 0;
  return true;
}

const VAMSYS_VWZZ_LOGIN_URL = "https://vamsys.io/login/vwzz";

function AttemptResult({ attempt, onReload, onContinueToVamsys, handoffState }) {
  const passed = attempt.status === "passed";
  const cancelled = attempt.status === "void";
  return (
    <section className={`intelex-exam-result ${passed ? "passed" : "failed"}`}>
      <span>
        {passed
          ? "Passed"
          : cancelled
            ? "Cancelled"
            : attempt.status === "timed_out"
              ? "Time expired"
              : "Not passed"}
      </span>
      <h1>
        {passed
          ? "Entry exam completed successfully"
          : cancelled
            ? "Exam attempt cancelled"
            : "Entry exam result"}
      </h1>
      {attempt.percentage !== null && <strong>{attempt.percentage}%</strong>}
      <p>
        {cancelled
          ? "This attempt has ended and counts as one of your permitted tries."
          : attempt.isTestSubmission
            ? "This result is recorded as a local test and does not affect any vAMSYS registration."
            : passed
              ? attempt.approvalStatus === "succeeded"
                ? "Your vWZZ registration has been approved. Continue to vAMSYS to access your pilot account."
                : attempt.approvalStatus === "failed"
                  ? "Your exam result is safely recorded, but registration approval requires staff attention. You can continue to vAMSYS while the review is completed."
                  : "Your exam result has been recorded and your vWZZ registration is being processed. Continue to vAMSYS for the next step."
              : "Review the retake rules below to see when another attempt is available."}
      </p>
      {attempt.isTestSubmission && (
        <div className="intelex-alert">
          Test submission recorded. Discord notification:{" "}
          {attempt.discordNotificationStatus === "sent"
            ? "sent successfully"
            : attempt.discordNotificationStatus === "not_configured"
              ? "webhook is not configured"
              : "delivery failed; administrators can check the server log"}
          . No vAMSYS registration action was performed.
        </div>
      )}
      {passed && !attempt.isTestSubmission ? (
        <>
          {handoffState.error && (
            <div className="intelex-alert" role="alert">
              {handoffState.error}
            </div>
          )}
          <button
            type="button"
            className="intelex-primary-button"
            disabled={handoffState.working}
            onClick={onContinueToVamsys}
          >
            {handoffState.working ? "Signing out..." : "Continue to vAMSYS"}
          </button>
        </>
      ) : (
        <button
          type="button"
          className="intelex-primary-button"
          onClick={onReload}
        >
          Return to exam overview
        </button>
      )}
    </section>
  );
}

function ActiveExam({ attempt, onFinished, onReload }) {
  const [answers, setAnswers] = useState(() =>
    Object.fromEntries(
      (attempt.questions || []).map((question) => [
        question.id,
        question.answer,
      ]),
    ),
  );
  const [remaining, setRemaining] = useState(
    () => new Date(attempt.deadlineAt).getTime() - Date.now(),
  );
  const [state, setState] = useState({
    saving: false,
    error: "",
    violations: attempt.violationCount || 0,
  });
  const [contentConcealed, setContentConcealed] = useState(false);
  const saveTimers = useRef(new Map());

  const sendEvents = useCallback(
    async (events, keepalive = false) => {
      if (!events.length) return;
      try {
        const response = await fetch(
          `/api/portal/exams/attempts/${attempt.id}/events`,
          {
            method: "POST",
            credentials: "include",
            keepalive,
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              "X-CSRF-Token": intelexCsrfToken(),
            },
            body: JSON.stringify({ events }),
          },
        );
        const payload = await response.json().catch(() => ({}));
        if (response.status === 409 || payload.data?.status === "failed") {
          onReload();
          return;
        }
        if (payload.data)
          setState((current) => ({
            ...current,
            violations: payload.data.violationCount,
          }));
      } catch (_error) {
        /* A reconnect is logged by the next successful heartbeat. */
      }
    },
    [attempt.id, onReload],
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      const next = new Date(attempt.deadlineAt).getTime() - Date.now();
      setRemaining(next);
      if (next <= 0) {
        window.clearInterval(timer);
        onReload();
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [attempt.deadlineAt, onReload]);

  useEffect(() => {
    const sessionKey = `interep-exam-connected-${attempt.id}`;
    try {
      if (window.sessionStorage.getItem(sessionKey) === "1")
        sendEvents([{ type: "reconnected" }]);
      window.sessionStorage.setItem(sessionKey, "1");
    } catch (_error) {
      // Integrity tracking continues when session storage is unavailable.
    }
    const heartbeat = window.setInterval(
      () => sendEvents([{ type: "heartbeat" }]),
      15000,
    );
    let screenshotTimer = null;
    let lastPrintEventAt = 0;
    const visibility = () => {
      if (document.hidden) setContentConcealed(true);
      sendEvents(
        [{ type: document.hidden ? "visibility_hidden" : "visibility_visible" }],
        document.hidden,
      );
    };
    const blur = () => {
      setContentConcealed(true);
      sendEvents([{ type: "window_blur" }], true);
    };
    const focus = () => {
      setContentConcealed(false);
      sendEvents([{ type: "window_focus" }]);
    };
    const fullscreen = () => {
      sendEvents([
        {
          type: document.fullscreenElement
            ? "fullscreen_enter"
            : "fullscreen_exit",
        },
      ]);
    };
    const block = (type) => (event) => {
      event.preventDefault();
      sendEvents([{ type }]);
    };
    const copy = block("copy_attempt");
    const cut = block("cut_attempt");
    const paste = block("paste_attempt");
    const context = block("context_menu");
    const concealAfterScreenshotAttempt = () => {
      setContentConcealed(true);
      if (screenshotTimer) window.clearTimeout(screenshotTimer);
      screenshotTimer = window.setTimeout(() => {
        if (!document.hidden && document.hasFocus()) setContentConcealed(false);
      }, 1400);
    };
    const isScreenshotShortcut = (event) =>
      event.key === "PrintScreen" ||
      event.code === "PrintScreen" ||
      (event.metaKey && event.shiftKey && ["3", "4", "5"].includes(event.key));
    const keydown = (event) => {
      if (isScreenshotShortcut(event)) {
        event.preventDefault();
        event.stopPropagation();
        concealAfterScreenshotAttempt();
        sendEvents([{ type: "screenshot_attempt" }]);
        navigator.clipboard?.writeText("").catch(() => {});
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "p") {
        event.preventDefault();
        event.stopPropagation();
        lastPrintEventAt = Date.now();
        sendEvents([{ type: "print_attempt" }]);
      }
    };
    const beforePrint = () => {
      setContentConcealed(true);
      if (Date.now() - lastPrintEventAt > 750)
        sendEvents([{ type: "print_attempt" }]);
    };
    const afterPrint = () => {
      if (!document.hidden && document.hasFocus()) setContentConcealed(false);
    };
    const unload = (event) => {
      event.preventDefault();
      event.returnValue = "";
      sendEvents([{ type: "page_exit" }], true);
    };
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("blur", blur);
    window.addEventListener("focus", focus);
    document.addEventListener("fullscreenchange", fullscreen);
    document.addEventListener("copy", copy);
    document.addEventListener("cut", cut);
    document.addEventListener("paste", paste);
    document.addEventListener("contextmenu", context);
    window.addEventListener("keydown", keydown, true);
    window.addEventListener("beforeprint", beforePrint);
    window.addEventListener("afterprint", afterPrint);
    window.addEventListener("beforeunload", unload);
    return () => {
      window.clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("blur", blur);
      window.removeEventListener("focus", focus);
      document.removeEventListener("fullscreenchange", fullscreen);
      document.removeEventListener("copy", copy);
      document.removeEventListener("cut", cut);
      document.removeEventListener("paste", paste);
      document.removeEventListener("contextmenu", context);
      window.removeEventListener("keydown", keydown, true);
      window.removeEventListener("beforeprint", beforePrint);
      window.removeEventListener("afterprint", afterPrint);
      window.removeEventListener("beforeunload", unload);
      if (screenshotTimer) window.clearTimeout(screenshotTimer);
    };
  }, [attempt.id, sendEvents]);

  useEffect(
    () => () => {
      saveTimers.current.forEach((timer) => window.clearTimeout(timer));
      saveTimers.current.clear();
    },
    [],
  );

  const persist = async (target, value = answers[target.id]) => {
    if (!target) return;
    await intelexRequest(`/api/portal/exams/attempts/${attempt.id}/answers`, {
      method: "PUT",
      body: { questionId: target.id, answer: value ?? null },
    });
  };
  const updateAnswer = (target, value) => {
    setAnswers((current) => ({ ...current, [target.id]: value }));
    setState((current) => ({ ...current, error: "" }));
    const previous = saveTimers.current.get(target.id);
    if (previous) window.clearTimeout(previous);
    saveTimers.current.set(
      target.id,
      window.setTimeout(async () => {
        saveTimers.current.delete(target.id);
        try {
          await persist(target, value);
        } catch (error) {
          setState((current) => ({
            ...current,
            error:
              error.message ||
              "An answer could not be saved. Check your connection before submitting.",
          }));
        }
      }, 650),
    );
  };
  const submit = async () => {
    const missingQuestion = attempt.questions.find(
      (item) => item.required && !answerComplete(item, answers[item.id]),
    );
    if (missingQuestion) {
      setState((current) => ({
        ...current,
        error: "Complete every required question before submitting the exam.",
      }));
      document
        .getElementById(`exam-question-${missingQuestion.id}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const unanswered = attempt.questions.filter(
      (item) => !answerComplete(item, answers[item.id]),
    ).length;
    if (
      !(await confirmAction({
        title: "Submit entry exam",
        message: unanswered
          ? `${unanswered} questions are unanswered. Submit the exam with those answers left blank?`
          : "Submit your final answers? You cannot change them after submission.",
        confirmLabel: "Submit exam",
      }))
    )
      return;
    setState((current) => ({ ...current, saving: true, error: "" }));
    try {
      saveTimers.current.forEach((timer) => window.clearTimeout(timer));
      saveTimers.current.clear();
      const result = dataItem(
        await intelexRequest(
          `/api/portal/exams/attempts/${attempt.id}/submit`,
          { method: "POST", body: { answers } },
        ),
      );
      onFinished(result);
      if (document.fullscreenElement)
        window.setTimeout(() => document.exitFullscreen().catch(() => {}), 50);
    } catch (error) {
      const missingId = error.details?.questionIds?.[0];
      const missingIndex = attempt.questions.findIndex(
        (item) => String(item.id) === String(missingId),
      );
      setState((current) => ({
        ...current,
        saving: false,
        error:
          missingIndex >= 0
            ? `Question ${missingIndex + 1} is incomplete. Complete every required part before submitting.`
            : error.message || "The exam could not be submitted.",
      }));
      if (missingIndex >= 0)
        document
          .getElementById(`exam-question-${missingId}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };
  const cancel = async () => {
    if (
      !(await confirmAction({
        title: "Cancel exam attempt",
        message:
          "This attempt will end immediately and count as one of your permitted tries.",
        confirmLabel: "Cancel attempt",
        tone: "danger",
      }))
    )
      return;
    setState((current) => ({ ...current, saving: true, error: "" }));
    saveTimers.current.forEach((timer) => window.clearTimeout(timer));
    saveTimers.current.clear();
    try {
      const cancelled = dataItem(
        await intelexRequest(
          `/api/portal/exams/attempts/${attempt.id}/cancel`,
          { method: "POST" },
        ),
      );
      onFinished(cancelled);
      if (document.fullscreenElement)
        window.setTimeout(() => document.exitFullscreen().catch(() => {}), 50);
    } catch (error) {
      setState((current) => ({
        ...current,
        saving: false,
        error: error.message || "The exam attempt could not be cancelled.",
      }));
    }
  };
  if (!attempt.questions?.length)
    return <div className="intelex-alert">This attempt has no questions.</div>;
  const answered = attempt.questions.filter((question) =>
    answerComplete(question, answers[question.id]),
  ).length;
  return (
    <div className={`intelex-exam-secure${contentConcealed ? " is-concealed" : ""}`}>
      {contentConcealed && (
        <div className="intelex-exam-concealment" role="status">
          <strong>Exam content protected</strong>
          <span>Return to the active exam window to continue.</span>
        </div>
      )}
      <header className="intelex-exam-run-header">
        <div>
          <span>Entry assessment</span>
          <strong>
            {answered} of {attempt.questions.length} answered
          </strong>
        </div>
        <div>
          <span>Time remaining</span>
          <strong className={remaining < 300000 ? "urgent" : ""}>
            {remainingText(remaining)}
          </strong>
        </div>
        <div>
          <span>Focus violations</span>
          <strong>{state.violations}</strong>
        </div>
      </header>
      <div className="intelex-exam-progress">
        <i
          style={{
            width: `${(answered / attempt.questions.length) * 100}%`,
          }}
        />
      </div>
      {state.error && (
        <div className="intelex-exam-save-alert intelex-alert" role="alert">
          {state.error}
        </div>
      )}
      <main className="intelex-exam-question-sheet">
        {attempt.questions.map((question, index) => (
          <article
            className="intelex-exam-question-card"
            id={`exam-question-${question.id}`}
            key={question.id}
          >
            <header>
              <span>{index + 1}</span>
              <div>
                <small>{question.sectionTitle}</small>
                <strong>
                  {question.points > 0
                    ? `${question.points} ${question.points === 1 ? "point" : "points"}`
                    : "Applicant information"}
                  {question.required ? " / required" : " / optional"}
                </strong>
              </div>
            </header>
            <h2>{question.prompt}</h2>
            {question.help && <p>{question.help}</p>}
            <ExamQuestion
              question={question}
              answer={answers[question.id]}
              onChange={(value) => updateAnswer(question, value)}
            />
          </article>
        ))}
      </main>
      <div className="intelex-exam-navigation">
        <span>Answers save automatically while this page remains open.</span>
        <div>
          <button
            type="button"
            className="intelex-danger-button"
            disabled={state.saving}
            onClick={cancel}
          >
            Cancel attempt
          </button>
          <button
            type="button"
            className="intelex-primary-button"
            disabled={state.saving}
            onClick={submit}
          >
            {state.saving ? "Please wait..." : "Submit exam"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ExamPortal({ user }) {
  const [overview, setOverview] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [result, setResult] = useState(null);
  const [reactivationState, setReactivationState] = useState({
    working: false,
    completed: false,
    error: "",
  });
  const [handoffState, setHandoffState] = useState({
    working: false,
    error: "",
  });
  const [state, setState] = useState({
    loading: true,
    starting: false,
    error: "",
  });
  const canTakeExam = ["applicant", "test"].includes(user.role);
  const load = useCallback(async () => {
    try {
      const payload = await intelexRequest("/api/portal/exams/current");
      setOverview(payload);
      setResult(null);
      const activeId = payload.eligibility?.activeAttemptId;
      if (activeId)
        setAttempt(
          dataItem(
            await intelexRequest(`/api/portal/exams/attempts/${activeId}`),
          ),
        );
      else setAttempt(null);
      setState({ loading: false, starting: false, error: "" });
    } catch (error) {
      setState({
        loading: false,
        starting: false,
        error: error.message || "The entry exam could not be loaded.",
      });
    }
  }, []);
  useEffect(() => {
    if (canTakeExam) load();
    else setState({ loading: false, starting: false, error: "" });
  }, [canTakeExam, load]);
  const start = async () => {
    const exam = overview?.data;
    if (!exam) return;
    setState((current) => ({ ...current, starting: true, error: "" }));
    try {
      if (exam.requireFullscreen && !document.fullscreenElement)
        await document.documentElement.requestFullscreen();
      const created = dataItem(
        await intelexRequest(`/api/portal/exams/${exam.id}/start`, {
          method: "POST",
        }),
      );
      setAttempt(
        dataItem(
          await intelexRequest(`/api/portal/exams/attempts/${created.id}`),
        ),
      );
      setState({ loading: false, starting: false, error: "" });
    } catch (error) {
      if (document.fullscreenElement)
        await document.exitFullscreen().catch(() => {});
      setState({
        loading: false,
        starting: false,
        error: error.message || "The exam could not be started.",
      });
    }
  };
  const reactivate = async () => {
    if (
      !(await confirmAction({
        title: "Reactivate vWZZ account",
        message:
          "Your previously passed entry exam will be reused and your current pending vAMSYS registration will be approved.",
        confirmLabel: "Reactivate account",
      }))
    )
      return;
    setReactivationState({ working: true, completed: false, error: "" });
    try {
      await intelexRequest("/api/portal/exams/reactivate-registration", {
        method: "POST",
      });
      setReactivationState({ working: false, completed: true, error: "" });
    } catch (error) {
      setReactivationState({
        working: false,
        completed: false,
        error: error.message || "Your vWZZ registration could not be reactivated.",
      });
    }
  };
  const continueToVamsys = async () => {
    setHandoffState({ working: true, error: "" });
    try {
      const response = await fetch("/api/portal/auth/logout", {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": intelexCsrfToken(),
        },
      });
      if (!response.ok && response.status !== 401)
        throw new Error("Interep could not end your session. Please try again.");
      window.location.assign(VAMSYS_VWZZ_LOGIN_URL);
    } catch (error) {
      setHandoffState({
        working: false,
        error: error.message || "Interep could not continue to vAMSYS.",
      });
    }
  };
  if (!canTakeExam)
    return (
      <div className="intelex-main">
        <div className="intelex-alert">
          The entry exam is available only to pending applicants and authorised
          test accounts.
        </div>
      </div>
    );
  if (state.loading)
    return (
      <div className="intelex-main">
        <div className="intelex-loading">Loading entry exam...</div>
      </div>
    );
  if (attempt?.status === "in_progress")
    return (
      <div className="intelex-main intelex-exam-page">
        <ActiveExam
          attempt={attempt}
          onFinished={(next) => {
            setAttempt(null);
            setResult(next);
          }}
          onReload={load}
        />
      </div>
    );
  if (result)
    return (
      <div className="intelex-main intelex-exam-page">
        <AttemptResult
          attempt={result}
          onReload={load}
          onContinueToVamsys={continueToVamsys}
          handoffState={handoffState}
        />
      </div>
    );
  const exam = overview?.data;
  const eligibility = overview?.eligibility || {};
  const registrationCheck = overview?.registrationCheck || {
    required: !user.isTestAccount,
    active: Boolean(user.isTestAccount),
    status: user.isTestAccount ? "not_required" : "missing",
  };
  return (
    <div className="intelex-main intelex-exam-page">
      <header className="intelex-page-heading">
        <div>
          <h1>Entry exam</h1>
          <p>
            {user.isTestAccount
              ? "This is a local test attempt and cannot approve a vAMSYS registration."
              : `Your result is linked securely to registration #${user.registrationId || "pending"}.`}
          </p>
        </div>
      </header>
      <section
        className="intelex-responder-identity"
        aria-label="Authenticated exam applicant"
      >
        <div>
          <span>Applicant username</span>
          <strong>{user.username || "Applicant"}</strong>
        </div>
        <div>
          <span>vAMSYS user</span>
          <strong>
            {user.vamsysUserId ? `#${user.vamsysUserId}` : "Local test account"}
          </strong>
        </div>
      </section>
      {state.error && (
        <div className="intelex-alert" role="alert">
          {state.error}
        </div>
      )}
      {reactivationState.error && (
        <div className="intelex-alert" role="alert">
          {reactivationState.error}
        </div>
      )}
      {handoffState.error && (
        <div className="intelex-alert" role="alert">
          {handoffState.error}
        </div>
      )}
      {exam ? (
        <>
          <section className="intelex-exam-introduction">
            <div>
              <span>vWZZ entry assessment</span>
              <h2>{exam.title}</h2>
              <p>{exam.description}</p>
            </div>
            <dl>
              <div>
                <dt>Passing grade</dt>
                <dd>{exam.passingPercentage}%</dd>
              </div>
              <div>
                <dt>Time limit</dt>
                <dd>{exam.timeLimitMinutes} minutes</dd>
              </div>
              <div>
                <dt>Retakes</dt>
                <dd>
                  {exam.allowRetake
                    ? `After ${exam.retakeDelayHours} hours`
                    : "Not available"}
                </dd>
              </div>
              <div>
                <dt>Attempts</dt>
                <dd>{exam.maxAttempts || "Unlimited"}</dd>
              </div>
            </dl>
          </section>
          <section className="intelex-exam-instructions">
            <h2>Before you start</h2>
            <p>{exam.instructions}</p>
            <ul>
              <li>The exam runs against server time and cannot be paused.</li>
              <li>
                Questions may be selected and ordered differently for each
                attempt.
              </li>
              <li>
                Leaving the tab, losing window focus and exiting fullscreen are
                logged as focus violations.
              </li>
              <li>
                Copying, pasting and opening the context menu are blocked and
                logged, but do not increase the focus-violation count.
              </li>
              <li>
                Printing and browser-recognised screenshot commands are blocked
                and logged. Exam content is concealed whenever the window is not
                active.
              </li>
              <li>
                Excessive focus violations may automatically fail the attempt
                according to this exam's settings.
              </li>
            </ul>
            {eligibility.reason === "cooldown" && (
              <div className="intelex-inline-notice">
                Another attempt is available after{" "}
                {displayDate(eligibility.eligibleAt)}.
              </div>
            )}
            {eligibility.reason === "passed" && (
              overview?.reactivation?.available ? (
                reactivationState.completed ? (
                  <div className="intelex-success intelex-reactivation-notice">
                    <div>
                      <strong>Your vWZZ registration has been approved.</strong>
                      <span>
                        Continue to vAMSYS to access your reactivated pilot
                        account.
                      </span>
                    </div>
                    <button
                      type="button"
                      className="intelex-primary-button"
                      disabled={handoffState.working}
                      onClick={continueToVamsys}
                    >
                      {handoffState.working
                        ? "Signing out..."
                        : "Continue to vAMSYS"}
                    </button>
                  </div>
                ) : (
                  <div className="intelex-inline-notice intelex-reactivation-notice">
                    <span>
                      You have already passed this exam. A new pending vWZZ
                      registration was found for your account.
                    </span>
                    <button
                      type="button"
                      disabled={reactivationState.working}
                      onClick={reactivate}
                    >
                      {reactivationState.working
                        ? "Reactivating..."
                        : "Reactivate account"}
                    </button>
                  </div>
                )
              ) : (
                <div className="intelex-inline-notice intelex-reactivation-notice">
                  <span>
                    You have already passed this exam. Continue to vAMSYS to
                    access your vWZZ account.
                  </span>
                  <button
                    type="button"
                    disabled={handoffState.working}
                    onClick={continueToVamsys}
                  >
                    {handoffState.working
                      ? "Signing out..."
                      : "Continue to vAMSYS"}
                  </button>
                </div>
              )
            )}
            {eligibility.reason === "maximum_attempts" && (
              <div className="intelex-inline-notice error">
                The maximum number of attempts has been reached.
              </div>
            )}
            {eligibility.reason === "retake_disabled" && (
              <div className="intelex-inline-notice error">
                Retakes are not enabled for this exam.
              </div>
            )}
            {registrationCheck.required && !registrationCheck.active && (
              <div className="intelex-inline-notice error">
                {registrationCheck.status === "unavailable"
                  ? "Interep could not verify your vAMSYS registration right now. Starting the exam remains blocked until verification succeeds."
                  : "No active vWZZ registration was found. Register on vAMSYS before starting the entry exam."}
              </div>
            )}
            <div className="intelex-exam-start-actions">
              <button
                type="button"
                className="intelex-primary-button intelex-exam-start"
                disabled={!eligibility.eligible || state.starting}
                onClick={start}
              >
                {state.starting
                  ? "Starting secure session..."
                  : "Enter fullscreen and begin"}
              </button>
              {registrationCheck.required && !registrationCheck.active && (
                <a
                  className="intelex-secondary-button intelex-exam-register"
                  href="https://vamsys.io/register/vwzz"
                  target="_blank"
                  rel="noreferrer"
                >
                  Register on vAMSYS
                </a>
              )}
            </div>
          </section>
        </>
      ) : (
        <div className="intelex-empty">
          <strong>No entry exam is currently published</strong>
          <p>Please return later or contact vWZZ staff.</p>
        </div>
      )}
      {overview?.attempts?.length > 0 && (
        <section className="intelex-exam-history">
          <h2>Attempt history</h2>
          {overview.attempts.map((item) => (
            <article key={item.id}>
              <strong>{String(item.status).replace("_", " ")}</strong>
              <span>{displayDate(item.submittedAt || item.startedAt)}</span>
              <span>
                {item.percentage === null ? "-" : `${item.percentage}%`}
              </span>
              <span>{item.violationCount} focus violations</span>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}

export default function IntelexExamPage() {
  return (
    <IntelexFrame active="exam" title="Entry exam">
      {({ user }) => <ExamPortal user={user} />}
    </IntelexFrame>
  );
}
